import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import './AuthPortal.css';
import './RegisterPortal.css';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { useNotification } from "../../notification/NotificationContext";
import { persistAuth } from '../../utils/auth';
import CharacterScene from './AuthCharacters';
import AuthIntroAnimation from './AuthIntroAnimation';
import { MailIcon, LockIcon, UserIcon, EyeIcon, EyeOffIcon, Spinner } from './AuthIcons';

const RegisterPortal = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [registerFailed, setRegisterFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [introDone, setIntroDone] = useState(() => {
    return sessionStorage.getItem('authIntroPlayed') === '1';
  });
  const initiallySkippedIntroRef = useRef(introDone);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const isTyping = !!focusedField;
  const passwordValue = formData.password || formData.confirmPassword;
  const passwordRevealed = showPassword || showConfirmPassword;

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!registerFailed) return;
    const t = setTimeout(() => setRegisterFailed(false), 1800);
    return () => clearTimeout(t);
  }, [registerFailed]);

  const handleIntroComplete = () => {
    sessionStorage.setItem('authIntroPlayed', '1');
    setIntroDone(true);
  };

  const handleGoogleSignup = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        const accessToken = tokenResponse.access_token;
        if (!accessToken) {
          showNotification("Google did not return an access token.", "warning");
          return;
        }

        const userInfoRes = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        const userInfo = userInfoRes.data;

        const res = await axios.post(`${API_BASE_URL}/api/auth/google`, {
          email: userInfo.email,
          fullName: userInfo.name,
          googleId: userInfo.sub,
        });

        const data = res.data;

        persistAuth(data.token, { fullName: data.fullName, email: data.email });

        showNotification(data.isNewUser ? "Google signup successful!" : "Google login successful!", "success");
        setTimeout(() => navigate('/dashboard'), 500);
      } catch (err) {
        console.error(err);
        const msg =
          err?.response?.data?.message || "Google signup failed. Please try again.";
        showNotification(msg, "error");
        setRegisterFailed(true);
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      showNotification("Google signup was cancelled or failed.", "error");
      setRegisterFailed(true);
      setGoogleLoading(false);
    },
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (formData.password !== formData.confirmPassword) {
      showNotification("Passwords do not match", "warning");
      setRegisterFailed(true);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          password: formData.password,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => null);
        const msg = errorData?.message || "Registration failed";
        showNotification(msg, "error");
        setRegisterFailed(true);
        return;
      }

      const data = await res.json();

      persistAuth(data.token, {
        fullName: data.fullName,
        email: data.email,
        emailConfirmed: data.emailConfirmed,
      });

      if (data.emailConfirmed === false) {
        localStorage.setItem('pendingEmail', data.email);
        showNotification("Registration successful! Please verify your email.", "success");
        setTimeout(() => navigate('/verify-email', { state: { email: data.email } }), 400);
        return;
      }

      showNotification("Registration successful!", "success");
      setTimeout(() => navigate('/dashboard'), 500);
    } catch (err) {
      console.error(err);
      showNotification("Something went wrong. Please try again.", "error");
      setRegisterFailed(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!introDone && <AuthIntroAnimation onComplete={handleIntroComplete} />}
      <div className="auth-portal">
        <div className="auth-content">
          <CharacterScene
            isTyping={isTyping}
            password={passwordValue}
            showPassword={passwordRevealed}
            loginFailed={registerFailed}
            entering={introDone}
          />

          <motion.div
            className="form-section"
            initial={initiallySkippedIntroRef.current ? false : { opacity: 0, x: 24 }}
            animate={introDone ? { opacity: 1, x: 0 } : { opacity: 0, x: 24 }}
            transition={{ duration: 0.55, delay: initiallySkippedIntroRef.current ? 0 : 0.65, ease: 'easeOut' }}
          >
            <div className="form-header">
              <div className="sparkle-icon">✦</div>
              <h1>Create Account</h1>
              <p className="subtitle">Join us today! Fill in your details</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={`form-group ${registerFailed ? 'has-error' : ''}`}>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField('')}
                  placeholder=" "
                  required
                />
                <span className="input-icon"><UserIcon /></span>
                <label className={formData.fullName ? 'float-up' : ''}>Full Name</label>
              </div>

              <div className={`form-group ${registerFailed ? 'has-error' : ''}`}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField('')}
                  placeholder=" "
                  required
                />
                <span className="input-icon"><MailIcon /></span>
                <label className={formData.email ? 'float-up' : ''}>Email</label>
              </div>

              <div className={`form-group ${registerFailed ? 'has-error' : ''}`}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField('')}
                  placeholder=" "
                  required
                />
                <span className="input-icon"><LockIcon /></span>
                <label className={formData.password ? 'float-up' : ''}>Password</label>
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>

              <div className={`form-group ${registerFailed ? 'has-error' : ''}`}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('confirmPassword')}
                  onBlur={() => setFocusedField('')}
                  placeholder=" "
                  required
                />
                <span className="input-icon"><LockIcon /></span>
                <label className={formData.confirmPassword ? 'float-up' : ''}>Confirm Password</label>
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>

              <div className="terms-checkbox">
                <label>
                  <input type="checkbox" required />
                  <span>I agree to the <a href="#terms">Terms & Conditions</a></span>
                </label>
              </div>

              <button
                type="submit"
                className="register-btn"
                disabled={loading || googleLoading}
              >
                {loading ? (
                  <>
                    <Spinner />
                    Creating account...
                  </>
                ) : (
                  'Sign Up'
                )}
              </button>

              <button
                type="button"
                className="google-btn"
                onClick={() => handleGoogleSignup()}
                disabled={loading || googleLoading}
              >
                {googleLoading ? (
                  <>
                    <Spinner />
                    Connecting Google...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 18 18">
                      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                      <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.335z"/>
                      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                    </svg>
                    Sign up with Google
                  </>
                )}
              </button>

              <div className="login-link">
                Already have an account? <Link to="/login">Log In</Link>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default RegisterPortal;
